import { Axial } from '../game/map/HexCoordinates';
import type { Refused } from '../game/buildings/Placed';

/**
 * Statki po drucie.
 *
 * **Jednostka w świecie, jak wóz** — stoi na polu, widzą ją wszyscy w zasięgu
 * i przyjeżdża tą samą drogą: lista obszaru po HTTP razem z terenem, zmiany
 * socketem. Różni się tym, czym statek różni się od wozu: powstaje na
 * zamówienie i trwa to tyle, ile mówi katalog.
 */

const API = import.meta.env.VITE_API_URL ?? '';

export interface ShipView extends Axial {
  id: string;
  owner: string;
  /**
   * Sekundy do końca budowy, gdy serwer to mówił; zero na statku gotowym.
   *
   * Odliczanie, a nie moment — ta sama decyzja, co przy budynkach: zegar
   * przeglądarki bywa przesunięty, więc klient odlicza sam od tego, co usłyszał.
   */
  readyIn: number;
  /** Ile trwa budowa w całości, żeby pasek miał mianownik. */
  takes: number;
  /**
   * Wersja sztandaru właściciela — statek nosi go na maszcie.
   *
   * **Czyj, a nie kto**: płynącego nie widać, bo jest pod pokładem, ale widać,
   * do kogo statek należy. Nieobecna u gracza bez sztandaru.
   */
  banner?: number;
  /**
   * Czy ktoś jest na pokładzie.
   *
   * **Pusty statek stoi przycumowany do krawędzi pola**, a wypływa na środek
   * dopiero wtedy, gdy ma kogo wieźć. Płynącego nie widać — jest pod pokładem —
   * więc to jedyna rzecz, po której widać, że statek nie jest porzucony.
   */
  crewed: boolean;
}

/** Co stoi na wodzie w tylu pierścieniach chunków od pola. */
export async function loadShips(at: Axial, radius: number): Promise<ShipView[]> {
  const response = await fetch(`${API}/api/world/ships/at?q=${at.q}&r=${at.r}&radius=${radius}`);
  if (!response.ok) {
    throw new Error(`statki: ${response.status}`);
  }
  return (await response.json()) as ShipView[];
}

/**
 * Zbuduj statek w swojej stoczni.
 *
 * Płaci **magazyn stoczni**, nie sakwa — dlatego przed tym trzeba tam surowce
 * przekazać, tak samo jak przed rozbudową budynku. Serwer sprawdza wszystko od
 * nowa i jego odpowiedź jest tą, która się liczy.
 */
export async function buildShip(
  token: string,
  building: string
): Promise<ShipView | Refused> {
  const response = await fetch(`${API}/api/world/ships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as ShipView) : (body as Refused);
}

/** Wsiądź na własny statek stojący obok. */
export async function boardShip(
  token: string,
  ship: string
): Promise<{ ship: string; q: number; r: number } | Refused> {
  const response = await fetch(`${API}/api/world/ships/board`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ship }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as { ship: string; q: number; r: number }) : (body as Refused);
}

/**
 * Zejdź na wskazany brzeg.
 *
 * Pole jedzie w żądaniu, a nie jest zgadywane po tamtej stronie: schodzi się
 * tam, gdzie się kliknęło, a nie na pierwsze pole, które serwerowi wpadnie.
 */
export async function leaveShip(
  token: string,
  at: Axial
): Promise<{ q: number; r: number; slot: number } | Refused> {
  const response = await fetch(`${API}/api/world/ships/leave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ q: at.q, r: at.r }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as { q: number; r: number; slot: number }) : (body as Refused);
}
